import { index, integer, sqliteTable, text } from 'drizzle-orm/sqlite-core';

/*
 * Indexes on these three tables are declared here rather than left to the query layer, and they are
 * not decorative. `DrizzleAppSchema` reads `getTableConfig(table).indexes` and passes them through to
 * the PowerSync table definition, so an index declared below is created on the local SQLite mirror
 * the next time the schema is applied at startup — no re-sync, since nothing about the synced data
 * changes. An index *not* declared below does not exist: PowerSync builds the local tables itself,
 * and the Postgres indexes in `files.sql` have no bearing on the copy on the phone.
 *
 * Without them every scoped read is a full scan of the mirror. That is invisible on a test account
 * and not on a real one, because these are the tables that grow without limit — a boat accumulates
 * files forever — and because the reads are reactive watches, re-run on every sync that touches the
 * table rather than once per screen.
 */

export const files = sqliteTable(
  'files',
  {
    id: text('id').notNull().primaryKey(),
    type: text('type'),
    name: text('name'),
    filename: text('filename'),
    alt: text('alt'),
    description: text('description'),
    notes: text('notes'),
    disk: text('disk'),
    extension: text('extension'),
    mime_type: text('mime_type'),
    dimensions: text('dimensions'),
    path: text('path'),
    issued_by: text('issued_by'),
    issuance_date: text('issuance_date'),
    visibility: text('visibility'),
    size: integer('size'),
    created_at: text('created_at'),
    updated_at: text('updated_at'),
    folder_id: integer('folder_id'),
    folder_uuid: text('folder_uuid'),
    expiration_date: text('expiration_date'),
    /**
     * How many `expiration_date_time_unit_type`s before the expiration date to remind.
     *
     * **An integer, because `files.expiration_date_time_unit` is `int4` in Postgres** — see
     * `files.sql`. `DrizzleAppSchema` derives the PowerSync column types from this table, and those
     * types are what the CRUD queue serializes: declared as text, a saved reminder went up as
     * `"2"` and the backend's `DateHelper::calculateReminderDate()` rejected it with a 500 for a
     * non-int argument, which then stalled the whole upload queue behind it. The GraphQL schema
     * (`Int`) and the REST types (`number`) have always said integer; this column was the outlier.
     */
    expiration_date_time_unit: integer('expiration_date_time_unit'),
    expiration_date_time_unit_type: text('expiration_date_time_unit_type'),
    has_expiration_date_reminder: integer('has_expiration_date_reminder'),
    laravel_id: integer('laravel_id'),
    organization_id: text('organization_id'),
  },
  (table) => [
    /*
     * A file's place in the tree, and the only column any view of a folder's contents filters on:
     * the list's `filesInScope`, the subtree counts, and the bulk delete's `IN (…)` over a whole
     * subtree of folder ids. Unindexed, opening any folder reads every file the account has.
     */
    index('files_folder_uuid').on(table.folder_uuid),
  ],
);

export const folders = sqliteTable(
  'folders',
  {
    id: text('id').notNull().primaryKey(),
    laravel_id: integer('laravel_id'),
    name: text('name'),
    created_at: text('created_at'),
    updated_at: text('updated_at'),
    user_id: integer('user_id'),
    organization_id: text('organization_id'),
    is_root: integer('is_root'),
    parent_id: integer('parent_id'),
    parent_uuid: text('parent_uuid'),
  },
  (table) => [
    /*
     * The edge the tree is walked along. `folderSubtreesCte` joins on it once per level of depth, so
     * without this a single delete or count of a nested folder scans `folders` up to
     * `MAX_FOLDER_DEPTH` times over; `foldersInScope` and the move's name-collision check filter on
     * it too.
     */
    index('folders_parent_uuid').on(table.parent_uuid),
    /*
     * For `useRootFolderQuery`, which is `WHERE is_root = 1 LIMIT 1` — a single row out of however
     * many folders exist, and the query the whole files feature is gated on, since "top level" means
     * the children of that row.
     *
     * Low cardinality, and deliberately still indexed: it is the *lookup* that is selective. Note
     * that `foldersInScope`'s other use of the column is negated — `is_root IS NULL OR is_root != 1`
     * — and will not use this index, nor should it, matching nearly every row as it does.
     */
    index('folders_is_root').on(table.is_root),
  ],
);

export const fileables = sqliteTable(
  'fileables',
  {
    id: text('id').notNull().primaryKey(),
    /**
     * The file's numeric backend id.
     *
     * **Read-only here, and not what anything joins on** — see `file_uuid`. The backend no longer
     * accepts it on a write (MDC-4776); a Postgres trigger fills it from `file_uuid`, so every row
     * that syncs down still carries both.
     *
     * @deprecated Join and write `file_uuid` instead.
     */
    file_id: integer('file_id'),
    /**
     * The file's uuid — the column every attachment read joins to `files.id` and every attachment
     * write sets.
     *
     * Not null in Postgres, nullable here for the usual mirror reason: a row this client has just
     * inserted is visible locally before the backend has acknowledged it.
     */
    file_uuid: text('file_uuid'),
    fileable_id: integer('fileable_id'),
    fileable_type: text('fileable_type'),
    fileable_uuid: text('fileable_uuid'),
    organization_id: text('organization_id'),
  },
  (table) => [
    /*
     * The join every attachment list runs — `files` inner-joined on `fileables.file_uuid` — and the
     * lookup behind the file drawer's "linked records". Unindexed, opening a record with one
     * attachment scans every link row the account has, and `fileables` grows without limit
     * alongside `files`.
     */
    index('fileables_file_uuid').on(table.file_uuid),
  ],
);

// export const fileables = new Table(
//   {
//     // id column (text) is automatically included
//     file_id: column.integer,
//     fileable_id: column.integer,
//     fileable_type: column.text,
//     fileable_uuid: column.text,
//   },
//   { indexes: {} },
// );

// export const folders = new Table(
//   {
//     // id column (text) is automatically included
//     name: column.text,
//     user_id: column.integer,
//     created_at: column.text,
//     updated_at: column.text,
//     is_root: column.integer,
//     parent_id: column.integer,
//   },
//   { indexes: {} },
// );

// export const files = new Table(
//   {
//     // id column (text) is automatically included
//     user_id: column.integer,
//     type: column.text,
//     name: column.text,
//     alt: column.text,
//     description: column.text,
//     disk: column.text,
//     extension: column.text,
//     mime_type: column.text,
//     dimensions: column.text,
//     path: column.text,
//     visibility: column.text,
//     size: column.integer,
//     created_at: column.text,
//     updated_at: column.text,
//     folder_id: column.integer,
//     expiration_date: column.text,
//     has_expiration_date_reminder: column.integer,
//     laravel_id: column.integer,
//   },
//   { indexes: {} },
// );
